import { useCallback, useEffect, useState } from 'react';

import { Checkbox } from 'primereact/checkbox';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { AuthenticationType } from '@/app/slices/authentication';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { useSites } from '@/services/sites';
import type { EquipmentResponse } from '@/services/stripping/types';
import { formatURLDate } from '@/utils';

import { useStrippingTechnicians } from '../../services/stripping/hooks';
import { useStrippingContext } from './useStrippingContext';

import './StrippingSelector.css';

export const StrippingSelector = () => {
  const me = useSelector(
    (state: { authentication: AuthenticationType }) => state.authentication.me,
  );

  const { setQcStripperTipLotNumber } = useStrippingContext();
  const [params, setParams] = useSearchParams();

  const equipmentId = me?.id ?? undefined;
  const [isTravelingTechnician, setIsTravelingTechnician] =
    useState(!!equipmentId);

  const fertilizationDate = params.get('date') || new Date().toString();
  const fertilizationSiteId = params.get('site');
  const strippingTechnicianId = params.get('tech');

  const { data: siteData } = useSites({ filter: 'fertilization' });

  const strippingTechnicians = useStrippingTechnicians({
    siteId: fertilizationSiteId,
    isTravelingTechnician,
  });

  const selectedTechnician = strippingTechnicianId
    ? strippingTechnicians?.find(
        (tech: EquipmentResponse) => tech.id === strippingTechnicianId,
      )
    : '';

  const selectedSite = fertilizationSiteId
    ? siteData?.find((site) => site.id === fertilizationSiteId)
    : '';

  const panelFooterTemplate = () => {
    return (
      <div className="stripping-technician-footer">
        <Checkbox
          onChange={(e) => setIsTravelingTechnician(!!e.checked)}
          checked={isTravelingTechnician}
        />
        <label>Show All Techs</label>
      </div>
    );
  };

  useEffect(() => {
    setQcStripperTipLotNumber('');
  }, [fertilizationDate, fertilizationSiteId, setQcStripperTipLotNumber]);

  const handleDateChange = useCallback(
    (e: DropdownChangeEvent) => {
      const newParams = new URLSearchParams(params);
      if (e.value) {
        newParams.set('date', formatURLDate(e.value));
      } else {
        newParams.delete('date');
      }
      setParams(newParams);
    },
    [params, setParams],
  );

  const handleSiteChange = useCallback(
    (e: DropdownChangeEvent) => {
      const newParams = new URLSearchParams(params);
      if (e.value) {
        newParams.set('site', e.value.id);
      } else {
        newParams.delete('site');
      }
      setParams(newParams);
    },
    [params, setParams],
  );

  const handleStrippingTechnicianChange = useCallback(
    (e: DropdownChangeEvent) => {
      const newParams = new URLSearchParams(params);

      if (e.value) {
        newParams.set('tech', e.value.id);
      } else {
        newParams.delete('tech');
      }
      setParams(newParams);
    },
    [params, setParams],
  );

  return (
    <div className="horizontal-form">
      <div className="stripping-dropdown-spacer">
        <Calendar
          value={new Date(fertilizationDate)}
          placeholder="Fertilization Date"
          dateFormat="mm-dd-yy"
          onChange={handleDateChange}
          showIcon
        />
      </div>
      <div className="stripping-dropdown-spacer">
        <Dropdown
          className="location-dropdown"
          value={selectedSite}
          options={siteData || []}
          optionLabel="name"
          placeholder="Fertilization Site"
          disabled={!fertilizationDate}
          onChange={handleSiteChange}
        />
      </div>
      <div className="stripping-dropdown-spacer">
        <Dropdown
          className="stripping-dropdown"
          value={selectedTechnician}
          options={strippingTechnicians || []}
          optionLabel="name"
          placeholder="Stripping Technician"
          panelFooterTemplate={panelFooterTemplate}
          disabled={!fertilizationDate}
          onChange={handleStrippingTechnicianChange}
        />
      </div>
    </div>
  );
};
